import { Suspense, type ReactNode } from 'react';
import { Canvas } from '@react-three/fiber';
import { Preload, AdaptiveDpr, AdaptiveEvents, BakeShadows } from '@react-three/drei';
import { useSlotTheme, useQualitySettings } from './hooks/useSlotTheme';
import { Lights, DefaultLights } from './components/Scene/Lights';
import { CameraController } from './components/Scene/Camera';
import type { RenderQuality } from './themes/types';

// =============================================================================
// Loading Fallback
// =============================================================================

function LoadingFallback() {
  return (
    <mesh>
      <boxGeometry args={[1, 1, 1]} />
      <meshStandardMaterial color="#333" wireframe />
    </mesh>
  );
}

// =============================================================================
// Scene Content
// =============================================================================

interface SceneContentProps {
  children: ReactNode;
  enableOrbitControls?: boolean;
}

function SceneContent({ children, enableOrbitControls = false }: SceneContentProps) {
  const { theme } = useSlotTheme();
  const qualitySettings = useQualitySettings();

  return (
    <>
      {/* Camera */}
      <CameraController
        enableControls={enableOrbitControls}
        position={[0, 2, 8]}
        target={[0, 0, 0]}
      />

      {/* Lighting */}
      {theme.cabinet.lights?.length ? <Lights /> : <DefaultLights />}

      {/* Performance optimizations based on quality */}
      <AdaptiveDpr pixelated />
      <AdaptiveEvents />
      {qualitySettings.shadows && <BakeShadows />}

      {/* Scene content */}
      <Suspense fallback={<LoadingFallback />}>{children}</Suspense>

      {/* Preload assets */}
      <Preload all />
    </>
  );
}

// =============================================================================
// Main Scene Component
// =============================================================================

interface SlotMachineSceneProps {
  /** Scene content (reels, cabinet, etc.) */
  children: ReactNode;
  /** Enable orbit controls for debugging */
  enableOrbitControls?: boolean;
  /** Custom class name */
  className?: string;
  /** Custom inline styles */
  style?: React.CSSProperties;
}

/**
 * React Three Fiber Canvas wrapper for the slot machine.
 * Handles WebGL context, rendering settings, and quality adaptation.
 */
export function SlotMachineScene({
  children,
  enableOrbitControls = false,
  className,
  style,
}: SlotMachineSceneProps) {
  const { renderQuality, theme } = useSlotTheme();
  const qualitySettings = useQualitySettings();

  // Don't render 3D canvas if quality is set to 2D
  if (renderQuality === '2d') {
    return (
      <div className={className} style={style}>
        {/* 2D fallback will be rendered by parent component */}
      </div>
    );
  }

  return (
    <div
      className={className}
      style={{
        background: theme.style.background,
        ...style,
      }}
    >
      <Canvas
        shadows={qualitySettings.shadows}
        dpr={getPixelRatio(renderQuality)}
        gl={{
          antialias: qualitySettings.antialiasing,
          powerPreference: 'high-performance',
          alpha: false,
        }}
        camera={{
          fov: 45,
          near: 0.1,
          far: 100,
          position: [0, 2, 8],
        }}
        onCreated={({ gl }) => {
          // Set background color
          gl.setClearColor(theme.style.reelBackground);
        }}
      >
        <SceneContent enableOrbitControls={enableOrbitControls}>
          {children}
        </SceneContent>
      </Canvas>
    </div>
  );
}

// =============================================================================
// Helpers
// =============================================================================

function getPixelRatio(quality: RenderQuality): [number, number] {
  switch (quality) {
    case 'high':
      return [1, 2];
    case 'medium':
      return [1, 1.5];
    case 'low':
      return [0.75, 1];
    default:
      return [1, 1];
  }
}

export default SlotMachineScene;
